import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { clone as cloneSkeleton } from 'three/examples/jsm/utils/SkeletonUtils.js';
import { HERO_ORDER, type HeroId, type MinionKind, type Team } from '@moba/shared';

import { t } from './i18n.ts';
export interface ClipTiming {
  start?: number;
  impact?: number;
  end?: number;
}

export interface CharacterManifestEntry {
  displayName: string;
  model: string;
  bodyHeight: number;
  targetHeight: number;
  equipment: string;
  clips: Record<string, string>;
  /** huesos de los pies (los del mapa de Blender): mantienen al héroe pegado al suelo en el idle */
  feet?: string[];
  /** claves de clip que se reproducen en bucle durante toda la acción (p. ej. el giro) */
  loop?: string[];
  /**
   * Recorte e impacto de los golpes (segundos del clip, de tools/blender/clip_metrics.py):
   * el tramo hasta `impact` dura lo que tarda la simulación en golpear.
   */
  timing?: Record<string, ClipTiming>;
  /** velocidad del pie apoyado en la carrera (u/s): el clip se acelera a la velocidad del héroe */
  runSpeed?: number;
  /** arco generado que aparece en la mano más adelantada mientras suena `clip` */
  bow?: { clip: string; bones: string[] };
  /** pistola generada que aparece en la mano más adelantada mientras suenan esos clips */
  blaster?: { clips: string[]; bones: string[] };
  /**
   * Pose procedural de arquero durante `bow.clip` (KayKit no trae animación de
   * arco): cadenas [brazo, antebrazo, mano] del brazo que sostiene el arco y del
   * que tensa la cuerda.
   */
  bowPose?: { bowArm: [string, string, string]; drawArm: [string, string, string] };
  /**
   * Copias fantasma (smear frames) que deja el héroe al moverse muy rápido
   * mientras suenan esos clips, p. ej. el Phantasm de Falco. Color CSS.
   */
  afterimages?: Array<{ clips: string[]; color: string }>;
  /** El cuerpo brilla de ese color mientras suenan esos clips (en llamas). */
  burn?: { clips: string[]; color: string };
  /**
   * El héroe se convierte en una bola de fuego de su tamaño (cometa que apunta
   * hacia donde corre, con estela de llamas) mientras suenan esos clips.
   */
  fireball?: { clips: string[] };
  /**
   * Salto visible durante esos clips (Super Jump Punch de Mario): el héroe sube
   * `height` y baja en arco a lo largo de la acción, con polvo al despegar y al
   * aterrizar y, si `coins`, monedas que saltan como en Melee.
   */
  jump?: { clips: string[]; height: number; coins?: boolean };
  /**
   * Golpe contra el suelo durante esos clips (E de Bowser): salta `height`,
   * cae acelerando y aterriza justo en el tick en que la simulación golpea
   * (`activeStart`), con aplastamiento del cuerpo, onda expansiva del radio
   * del golpe, polvo, piedras y temblor de cámara.
   */
  slam?: { clips: string[]; height: number };
  /**
   * Pose de escupir fuego (Q de Bowser) superpuesta al clip: el torso se
   * inclina, el cuello se estira hacia delante, la cabeza se alza un poco y
   * tiembla, y la mandíbula se abre. Nombres de huesos del rig.
   */
  breathPose?: { clips: string[]; chest: string; neck: string[]; head: string; jaw: string };
  /**
   * Giro procedural durante esos clips: `turns` vueltas completas en los
   * primeros `ticks` (o en toda la acción) y agacharse `crouch` × altura
   * (W de Bowser: se mete en el caparazón y gira; E de Falco al activar).
   */
  spin?: Array<{ clips: string[]; turns: number; crouch?: number; ticks?: number }>;
  /**
   * Agarre (R de Bowser): mientras el héroe está en el aire con su presa se
   * reproduce esta clave de clip, con el golpe sincronizado con el aterrizaje.
   */
  grabSlam?: string;
  /**
   * Aliento de fuego durante esos clips (Q de Bowser): partículas de llama que
   * salen de la boca (`bone` = hueso de la cabeza, desplazado `forward`/`down`
   * en fracciones de la altura) hacia donde mira, abiertas `arc` grados y con
   * alcance `range` (iguales al cono de daño), desde el tick `startTick`.
   */
  breath?: { clips: string[]; bone: string; forward: number; down: number; range: number; arc: number; startTick: number };
  /** Escudo hexagonal de energía (Shine/Reflector) que envuelve al héroe durante esos clips. */
  shield?: { clips: string[]; color: string };
  /**
   * Pose procedural del Reflector de Melee durante esos clips: de frente a la
   * cámara, en cuclillas con las piernas abiertas (IK, pies en el suelo), torso
   * erguido y brazos abiertos hacia abajo con las manos junto a la cadera.
   * Cadenas [columna, pecho, cabeza], [muslo, espinilla, pie] y
   * [brazo, antebrazo, mano]; `squat` es la fracción de la altura que bajan las caderas.
   */
  reflectorPose?: {
    clips: string[];
    spine: [string, string, string];
    legs: [[string, string, string], [string, string, string]];
    arms: [[string, string, string], [string, string, string]];
    squat: number;
  };
}

export interface CharacterAsset {
  id: HeroId;
  manifest: CharacterManifestEntry;
  scene: THREE.Group;
  clips: THREE.AnimationClip[];
}

export interface CharacterInstance {
  root: THREE.Group;
  model: THREE.Object3D;
  clips: THREE.AnimationClip[];
}

const KEEP_ACCESSORY = /helmet|cape|hat/i;

/** Huellas de contenido de los modelos (las calcula vite.config.ts al compilar). */
declare const __ASSET_HASHES__: Record<string, string> | undefined;
const ASSET_HASHES: Record<string, string> = typeof __ASSET_HASHES__ !== 'undefined' ? __ASSET_HASHES__ : {};

/** URL con la huella del archivo: un modelo nuevo nunca sale de una caché vieja. */
export function assetUrl(path: string): string {
  const hash = ASSET_HASHES[path];
  return hash ? `${path}?v=${hash}` : path;
}

export async function loadManifest(): Promise<Record<HeroId, CharacterManifestEntry>> {
  const response = await fetch(assetUrl('/characters/manifest.json'), { cache: 'no-cache' });
  if (!response.ok) throw new Error(t('No se pudo cargar el manifiesto ({status})', { status: response.status }));
  return (await response.json()) as Record<HeroId, CharacterManifestEntry>;
}

export async function loadCharacters(
  manifest: Record<HeroId, CharacterManifestEntry>,
  onProgress?: (fraction: number, label: string) => void,
): Promise<Map<HeroId, CharacterAsset>> {
  const loader = new GLTFLoader();
  const assets = new Map<HeroId, CharacterAsset>();
  let done = 0;

  for (const id of HERO_ORDER) {
    const entry = manifest[id];
    onProgress?.(done / HERO_ORDER.length, t('Cargando {name}…', { name: entry.displayName }));

    const gltf = await loader.loadAsync(assetUrl(entry.model));
    const scene = prepareScene(gltf.scene, entry);

    assets.set(id, {
      id,
      manifest: entry,
      scene,
      clips: gltf.animations,
    });

    done++;
    onProgress?.(done / HERO_ORDER.length, t('{name} listo', { name: entry.displayName }));
  }

  return assets;
}

function prepareScene(scene: THREE.Group, entry: CharacterManifestEntry): THREE.Group {
  const root = new THREE.Group();
  root.name = `${entry.displayName}_root`;
  root.add(scene);
  scene.scale.setScalar(entry.targetHeight / entry.bodyHeight);

  scene.traverse((object) => {
    const mesh = object as THREE.Mesh;
    if (!mesh.isMesh) return;

    const skinned = (mesh as THREE.SkinnedMesh).isSkinnedMesh;
    if (!skinned) {
      const keep = object.name === entry.equipment || KEEP_ACCESSORY.test(object.name);
      if (!keep) {
        mesh.visible = false;
        return;
      }
    }

    mesh.castShadow = true;
    mesh.receiveShadow = false;
    if (skinned) mesh.frustumCulled = false;
  });

  return root;
}

export function instantiateCharacter(asset: CharacterAsset): CharacterInstance {
  const model = cloneSkeleton(asset.scene);
  const root = new THREE.Group();
  root.name = `${asset.manifest.displayName}_instance`;
  root.add(model);
  return { root, model, clips: asset.clips };
}

export function makeGlowTexture(size = 128): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D no disponible');

  const half = size / 2;
  const gradient = ctx.createRadialGradient(half, half, 0, half, half, half);
  gradient.addColorStop(0, 'rgba(255,255,255,1)');
  gradient.addColorStop(0.25, 'rgba(255,255,255,0.75)');
  gradient.addColorStop(0.6, 'rgba(255,255,255,0.18)');
  gradient.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, size, size);

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

/**
 * Otros modelos estáticos (`assets/props/`): la pistola de Falco (WpnFoxBlaster
 * de Melee) y el caparazón de Koopa (icono del objeto). Si alguno falta, el
 * cliente usa su sustituto generado.
 */
export const propModels = new Map<string, THREE.Group>();

const PROP_MODELS: Record<string, string> = {
  blaster: '/props/blaster.glb',
  'koopa-shell-green': '/props/koopa-shell-green.glb',
};

export async function loadPropModels(): Promise<void> {
  const loader = new GLTFLoader();
  for (const [key, url] of Object.entries(PROP_MODELS)) {
    try {
      const gltf = await loader.loadAsync(assetUrl(url));
      gltf.scene.traverse((object) => {
        const mesh = object as THREE.Mesh;
        if (mesh.isMesh) mesh.castShadow = true;
      });
      propModels.set(key, gltf.scene);
    } catch (error) {
      console.warn(`[assets] no se pudo cargar ${url}`, error);
    }
  }
}

/** Retrato de un modelo estático, de tres cuartos, como data URL (iconos de objetos). */
export function renderPropIcon(model: THREE.Object3D, size = 96): string {
  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true });
  renderer.setSize(size, size);
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  const scene = new THREE.Scene();
  scene.add(new THREE.HemisphereLight(0xffffff, 0x334455, 2.2));
  const key = new THREE.DirectionalLight(0xffffff, 2.4);
  key.position.set(2, 4, 3);
  scene.add(key);
  const clone = model.clone(true);
  scene.add(clone);
  const box = new THREE.Box3().setFromObject(clone);
  const center = box.getCenter(new THREE.Vector3());
  const radius = box.getSize(new THREE.Vector3()).length() * 0.5;
  const camera = new THREE.PerspectiveCamera(30, 1, 0.01, 50);
  const dist = radius / Math.sin(THREE.MathUtils.degToRad(15)) * 1.02;
  camera.position.copy(center).add(new THREE.Vector3(0.55, 0.7, 1).normalize().multiplyScalar(dist));
  camera.lookAt(center);
  renderer.render(scene, camera);
  const url = renderer.domElement.toDataURL('image/png');
  renderer.dispose();
  renderer.forceContextLoss();
  return url;
}

/** Modelos estáticos de minions (`assets/props/`), uno por equipo. */
export type MinionAssetKey = `${MinionKind}-${Team}`;

const MINION_MODELS: Record<MinionAssetKey, string> = {
  'koopa-blue': '/props/koopa-green.glb',
  'koopa-red': '/props/koopa-red.glb',
};

export async function loadMinionModels(): Promise<Map<MinionAssetKey, THREE.Group>> {
  const loader = new GLTFLoader();
  const out = new Map<MinionAssetKey, THREE.Group>();
  for (const [key, url] of Object.entries(MINION_MODELS) as Array<[MinionAssetKey, string]>) {
    const gltf = await loader.loadAsync(assetUrl(url));
    gltf.scene.traverse((object) => {
      const mesh = object as THREE.Mesh;
      if (mesh.isMesh) mesh.castShadow = true;
    });
    out.set(key, gltf.scene);
  }
  return out;
}
